import React, { useState, useEffect, useRef, useCallback } from 'react';

/**
 * LetterEater — public game component
 * ────────────────────────────────────
 * Pac-Man-style maze game ported from c:\Users\jo\Desktop\word-eater-game-demo.html
 * into the DarijaPro React app.
 *
 * Props:
 *   - API: backend URL prefix
 *   - userToken: JWT for the current user (used for Pro check)
 *   - isDarkMode: app-wide dark mode flag (game has its own theme system)
 *   - showNotification: function for toast messages
 *   - onBack: returns to games list
 *   - hasPaidPlan: whether user has Pro membership
 */

// ─────────────────────────────────────────────────────────────────────────────
// Maze layouts — 13×11. Each has 3 tunnel rows (3, 5, 7) with open edges so
// the character can wrap around at any of those rows.
// ─────────────────────────────────────────────────────────────────────────────
const MAZE_LAYOUTS = [
  [
    [1,1,1,1,1,1,1,1,1,1,1,1,1],
    [1,0,0,0,0,0,1,0,0,0,0,0,1],
    [1,0,1,1,0,0,0,0,0,1,1,0,1],
    [0,0,0,0,0,1,0,1,0,0,0,0,0],
    [1,1,0,1,0,0,0,0,0,1,0,1,1],
    [0,0,0,1,0,0,0,0,0,1,0,0,0],
    [1,1,0,1,0,0,0,0,0,1,0,1,1],
    [0,0,0,0,0,1,0,1,0,0,0,0,0],
    [1,0,1,1,0,0,0,0,0,1,1,0,1],
    [1,0,0,0,0,0,1,0,0,0,0,0,1],
    [1,1,1,1,1,1,1,1,1,1,1,1,1],
  ],
  [
    [1,1,1,1,1,1,1,1,1,1,1,1,1],
    [1,0,0,0,0,0,0,0,0,0,0,0,1],
    [1,0,1,0,1,1,0,1,1,0,1,0,1],
    [0,0,1,0,0,0,0,0,0,0,1,0,0],
    [1,0,0,0,1,0,1,0,1,0,0,0,1],
    [0,0,0,0,0,0,0,0,0,0,0,0,0],
    [1,0,0,0,1,0,1,0,1,0,0,0,1],
    [0,0,1,0,0,0,0,0,0,0,1,0,0],
    [1,0,1,0,1,1,0,1,1,0,1,0,1],
    [1,0,0,0,0,0,0,0,0,0,0,0,1],
    [1,1,1,1,1,1,1,1,1,1,1,1,1],
  ],
  [
    [1,1,1,1,1,1,1,1,1,1,1,1,1],
    [1,0,0,0,0,0,1,0,0,0,0,0,1],
    [1,0,1,0,1,0,1,0,1,0,1,0,1],
    [0,0,0,0,1,0,0,0,1,0,0,0,0],
    [1,0,1,0,0,0,1,0,0,0,1,0,1],
    [0,0,0,0,0,0,0,0,0,0,0,0,0],
    [1,0,1,0,0,0,1,0,0,0,1,0,1],
    [0,0,0,0,1,0,0,0,1,0,0,0,0],
    [1,0,1,0,1,0,1,0,1,0,1,0,1],
    [1,0,0,0,0,0,1,0,0,0,0,0,1],
    [1,1,1,1,1,1,1,1,1,1,1,1,1],
  ],
  [
    [1,1,1,1,1,1,1,1,1,1,1,1,1],
    [1,0,0,0,0,0,0,0,0,0,0,0,1],
    [1,0,1,1,0,1,0,1,0,1,1,0,1],
    [0,0,1,0,0,0,0,0,0,0,1,0,0],
    [1,0,0,0,1,0,0,0,1,0,0,0,1],
    [0,0,0,0,0,0,0,0,0,0,0,0,0],
    [1,0,0,0,1,0,0,0,1,0,0,0,1],
    [0,0,1,0,0,0,0,0,0,0,1,0,0],
    [1,0,1,1,0,1,0,1,0,1,1,0,1],
    [1,0,0,0,0,0,0,0,0,0,0,0,1],
    [1,1,1,1,1,1,1,1,1,1,1,1,1],
  ],
];
const COLS = MAZE_LAYOUTS[0][0].length;
const ROWS = MAZE_LAYOUTS[0].length;
const PAC_SPAWN = { r: 5, c: 6 };
const TICK_MS = 140;

// ─────────────────────────────────────────────────────────────────────────────
// Themes (13 pastel gradients, each with matching text/panel/accent/pellet)
// ─────────────────────────────────────────────────────────────────────────────
const D_TEXT = '#f1f5f9', D_MUTED = '#94a3b8', D_PANEL = 'rgba(255,255,255,0.06)', D_STAR = '#ffffff';
const L_TEXT = '#0A0F1C', L_MUTED = '#5b6478', L_PANEL = 'rgba(10,15,28,0.06)',    L_STAR = '#3b6cec';
const THEMES = [
  { name: 'White',    gradient: 'linear-gradient(160deg, #ffffff 0%, #e8e8ed 100%)',
    text: L_TEXT, muted: L_MUTED, panel: L_PANEL, accent: '#3B6CEC', pellet: '#0A0F1C', star: L_STAR, wallStyle: 'soft' },
  { name: 'Dark',     gradient: 'linear-gradient(160deg, #0A0F1C 0%, #1a2235 100%)',
    text: D_TEXT, muted: D_MUTED, panel: D_PANEL, accent: '#B299E0', pellet: '#ffe066', star: D_STAR, wallStyle: 'glass' },
  { name: 'Mint',     gradient: 'linear-gradient(160deg, #E0F2F1 0%, #B2DFDB 100%)',
    text: '#064E3B', muted: '#115E59', panel: 'rgba(6,78,59,0.08)', accent: '#0F766E', pellet: '#0F766E', star: '#0F766E', wallStyle: 'soft' },
  { name: 'Sunset',   gradient: 'linear-gradient(160deg, #FFE5D9 0%, #FFCAD4 50%, #E0BBE4 100%)',
    text: '#4C0519', muted: '#831843', panel: 'rgba(76,5,25,0.08)', accent: '#9D174D', pellet: '#9D174D', star: '#9D174D', wallStyle: 'soft' },
  { name: 'Ocean',    gradient: 'linear-gradient(160deg, #B3E5FC 0%, #B2EBF2 100%)',
    text: '#0C4A6E', muted: '#075985', panel: 'rgba(12,74,110,0.08)', accent: '#0369A1', pellet: '#0369A1', star: '#0369A1', wallStyle: 'soft' },
  { name: 'Sahara',   gradient: 'linear-gradient(160deg, #FFF8E1 0%, #FFE0B2 100%)',
    text: '#451A03', muted: '#78350F', panel: 'rgba(69,26,3,0.08)', accent: '#92400E', pellet: '#92400E', star: '#92400E', wallStyle: 'soft' },
  { name: 'Blossom',  gradient: 'linear-gradient(160deg, #FCE4EC 0%, #E1BEE7 100%)',
    text: '#4A044E', muted: '#701A75', panel: 'rgba(74,4,78,0.08)', accent: '#86198F', pellet: '#86198F', star: '#86198F', wallStyle: 'soft' },
  { name: 'Tropical', gradient: 'linear-gradient(160deg, #B9F6CA 0%, #B2EBF2 50%, #BBDEFB 100%)',
    text: '#064E3B', muted: '#065F46', panel: 'rgba(6,78,59,0.08)', accent: '#047857', pellet: '#047857', star: '#047857', wallStyle: 'soft' },
  { name: 'Lavender', gradient: 'linear-gradient(160deg, #E8EAF6 0%, #D1C4E9 50%, #F8BBD0 100%)',
    text: '#2E1065', muted: '#5B21B6', panel: 'rgba(46,16,101,0.08)', accent: '#5B21B6', pellet: '#5B21B6', star: '#5B21B6', wallStyle: 'soft' },
  { name: 'Candy',    gradient: 'linear-gradient(160deg, #FFD6E0 0%, #C5E1FF 100%)',
    text: '#500724', muted: '#9D174D', panel: 'rgba(80,7,36,0.08)', accent: '#BE185D', pellet: '#BE185D', star: '#BE185D', wallStyle: 'soft' },
  { name: 'Frost',    gradient: 'linear-gradient(160deg, #E1F5FE 0%, #FFFFFF 50%, #B3E5FC 100%)',
    text: '#083344', muted: '#155E75', panel: 'rgba(8,51,68,0.08)', accent: '#0E7490', pellet: '#0E7490', star: '#0E7490', wallStyle: 'soft' },
  { name: 'Coral',    gradient: 'linear-gradient(160deg, #FFDDC1 0%, #B2EBF2 100%)',
    text: '#451A03', muted: '#7C2D12', panel: 'rgba(69,26,3,0.08)', accent: '#B45309', pellet: '#B45309', star: '#B45309', wallStyle: 'soft' },
  { name: 'Honey',    gradient: 'linear-gradient(160deg, #FFF5BA 0%, #FFE0B2 50%, #FFCDB2 100%)',
    text: '#451A03', muted: '#78350F', panel: 'rgba(69,26,3,0.08)', accent: '#A16207', pellet: '#A16207', star: '#A16207', wallStyle: 'soft' },
];

const CHARACTERS = [
  { id: 'blob',    label: 'Blob' },
  { id: 'cat',     label: 'Cat' },
  { id: 'robot',   label: 'Robot' },
  { id: 'alien',   label: 'Alien' },
  { id: 'pumpkin', label: 'Pumpkin' },
  { id: 'frog',    label: 'Frog' },
];

const PARTICLE_COLORS = ['#ff6b9d', '#ffd60a', '#67e8f9', '#b239e0', '#86efac'];
const CONFETTI_COLORS = ['#ff6b9d', '#ffd60a', '#67e8f9', '#b239e0', '#86efac', '#3b6cec'];

// ─────────────────────────────────────────────────────────────────────────────
// Sound engine (Web Audio, synthesized)
// ─────────────────────────────────────────────────────────────────────────────
function makeSfx() {
  let ctx = null;
  let enabled = (() => {
    try { return localStorage.getItem('letterEaterSound') !== 'off'; } catch { return true; }
  })();
  const getCtx = () => {
    if (ctx) {
      // Browsers start the context in "suspended" state until a user gesture.
      // Try to resume each time we touch it; harmless if already running.
      if (ctx.state === 'suspended') { try { ctx.resume(); } catch {} }
      return ctx;
    }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      ctx = new Ctx();
    } catch { return null; }
    return ctx;
  };
  const tone = (freq, durMs, type, vol, offMs) => {
    if (!enabled) return;
    const c = getCtx(); if (!c) return;
    // Skip silently until the context is actually running — avoids the noisy
    // "AudioContext was not allowed to start" warnings in the console.
    if (c.state !== 'running') return;
    const t0 = c.currentTime + (offMs || 0) / 1000;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = type || 'sine';
    o.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(vol || 0.18, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + durMs / 1000);
    o.connect(g).connect(c.destination);
    o.start(t0);
    o.stop(t0 + durMs / 1000 + 0.02);
  };
  const clap = (offMs, vol) => {
    if (!enabled) return;
    const c = getCtx(); if (!c) return;
    if (c.state !== 'running') return;
    const t0 = c.currentTime + (offMs || 0) / 1000;
    const dur = 0.06;
    const buf = c.createBuffer(1, Math.floor(c.sampleRate * dur), c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1);
    const src = c.createBufferSource();
    src.buffer = buf;
    const bp = c.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 1400;
    bp.Q.value = 0.8;
    const g = c.createGain();
    g.gain.setValueAtTime(vol || 0.22, t0);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    src.connect(bp).connect(g).connect(c.destination);
    src.start(t0);
    src.stop(t0 + dur + 0.02);
  };
  return {
    enabled: () => enabled,
    setEnabled: (v) => {
      enabled = !!v;
      try { localStorage.setItem('letterEaterSound', enabled ? 'on' : 'off'); } catch {}
      if (enabled) getCtx();
    },
    init: () => { getCtx(); },
    eat: () => { tone(660, 90, 'sine', 0.18); tone(880, 90, 'sine', 0.14, 45); },
    win: () => {
      const notes = [523.25, 659.25, 783.99, 1046.5];
      notes.forEach((f, i) => tone(f, 220, 'triangle', 0.22, i * 90));
      clap(380, 0.28); clap(480, 0.24); clap(580, 0.22); clap(680, 0.18);
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// CSS (scoped to .le-host)
// ─────────────────────────────────────────────────────────────────────────────
const STYLE = `
.le-host {
  --cell: 38px;
  --char: 32px;
  --pellet: #ffe066;
  --text: #f1f5f9;
  --muted: #8a8e9a;
  --bg-gradient: linear-gradient(160deg, #1a1a2e 0%, #050811 100%);
  --wall: #2554c7;
  --wall-edge: #5b87ff;
  --accent: #ff6b9d;
  --panel: rgba(255,255,255,0.06);
  --star: #ffffff;

  position: relative; width: 100%;
  min-height: calc(100vh - 80px);
  background: var(--bg-gradient);
  color: var(--text);
  font-family: 'Space Grotesk', system-ui, -apple-system, sans-serif;
  display: grid; place-items: center;        /* desktop: vertically + horizontally centered */
  padding: 8px 12px 16px;
  overflow: hidden;
  user-select: none; -webkit-user-select: none;
  touch-action: none;
  border-radius: 16px;
  transition: background 0.8s ease;
}
@media (max-width: 1023px) {
  .le-host {
    position: fixed; inset: 0; z-index: 1000;
    height: 100vh; min-height: 100vh;
    border-radius: 0; padding: 12px;
    place-items: start center;               /* mobile: top-aligned full-screen */
  }
}
@media (max-width: 640px) {
  .le-host { --cell: 26px; --char: 22px; }
}
.le-host * { box-sizing: border-box; margin: 0; padding: 0; -webkit-tap-highlight-color: transparent; }

.le-host .app { display: flex; flex-direction: column; gap: 10px; align-items: center; max-width: 100vw; position: relative; z-index: 2; }
.le-host h1 {
  font-size: 22px; font-weight: 900;
  color: #0A0F1C;
  letter-spacing: 0.3px;
  display: inline-flex; align-items: center; gap: 8px; justify-content: center;
  text-shadow: 0 1px 0 rgba(255,255,255,0.45), 0 3px 10px rgba(0,0,0,0.35), 0 0 18px rgba(255,255,255,0.25);
}
.le-host[data-wall-style="glass"] h1 {
  color: #ffffff;
  text-shadow: 0 2px 0 rgba(0,0,0,0.55), 0 4px 14px rgba(0,0,0,0.50), 0 0 22px rgba(178,153,224,0.30);
}
@media (max-width: 640px) { .le-host h1 { font-size: 18px; gap: 6px; } }

.le-host .title-icon { width: 18px; height: 18px; display: inline-block; position: relative; flex-shrink: 0; }
.le-host .title-icon .toon { position: absolute; inset: 0; }
@media (max-width: 640px) { .le-host .title-icon { width: 16px; height: 16px; } }

.le-host .meta {
  display: flex; gap: 8px; align-items: center; flex-wrap: wrap; justify-content: center;
  font-size: 12px; color: var(--muted); line-height: 1.4; text-align: center;
}
@media (max-width: 640px) { .le-host .meta { font-size: 11px; gap: 6px; } }

/* Top bar: back | title (center) | gear */
.le-host .topbar {
  position: relative; display: grid;
  grid-template-columns: 40px 1fr 40px;
  align-items: center; gap: 12px;
  width: 100%; max-width: 540px;
  margin-bottom: -4px;
}
.le-host .topbar h1 { text-align: center; grid-column: 2; margin: 0; }
.le-host .icon-btn {
  width: 38px; height: 38px;
  border-radius: 50%;
  background: var(--panel);
  border: none; color: var(--text);
  cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  padding: 0;
  transition: background 0.15s, transform 0.2s, color 0.5s ease;
}
.le-host .icon-btn svg { display: block; }
.le-host .icon-btn:hover { background: var(--panel); filter: brightness(1.3); }
.le-host .back-btn:hover { transform: translateX(-2px); }
.le-host .gear-btn:hover { transform: rotate(45deg); }

/* Maze */
.le-host .maze-wrap { background: transparent; padding: 10px; border-radius: 16px; box-shadow: 0 25px 70px rgba(0,0,0,0.3); }
.le-host .maze {
  position: relative; display: grid;
  grid-template-columns: repeat(13, var(--cell));
  grid-template-rows: repeat(11, var(--cell));
  background: transparent; border-radius: 10px; overflow: hidden;
}
.le-host .maze::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(circle 80px at var(--gx, 50%) var(--gy, 50%), color-mix(in srgb, var(--accent) 10%, transparent), transparent 70%);
  pointer-events: none; transition: background 0.3s ease; z-index: 1;
}
.le-host .cell { width: var(--cell); height: var(--cell); position: relative; }
.le-host .cell.wall {
  background: linear-gradient(135deg, var(--wall-edge) 0%, var(--wall) 55%, rgba(0,0,0,0.35) 115%);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -2px 3px rgba(0,0,0,0.35), inset 0 0 0 1px rgba(255,255,255,0.06);
  border-radius: 6px;
  transition: background 0.5s ease, box-shadow 0.5s ease;
}
.le-host[data-wall-style="glass"] .cell.wall {
  background: rgba(255,255,255,0.06);
  backdrop-filter: blur(8px); -webkit-backdrop-filter: blur(8px);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.40), inset 0 -1px 0 rgba(0,0,0,0.20), 0 4px 10px rgba(0,0,0,0.35);
  border: 1px solid rgba(255,255,255,0.18);
}
.le-host[data-wall-style="soft"] .cell.wall {
  background: rgba(15,23,42,0.22);
  backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -1px 0 rgba(0,0,0,0.10), 0 3px 6px rgba(0,0,0,0.10);
  border: 1px solid rgba(15,23,42,0.08);
}

/* Pellets */
.le-host .pellet {
  position: absolute; inset: 3px; border-radius: 50%;
  background: color-mix(in srgb, var(--pellet) 14%, transparent);
  color: var(--pellet);
  font-weight: 900; display: grid; place-items: center;
  font-size: calc(var(--cell) * 0.62); line-height: 1;
  box-shadow: 0 0 10px color-mix(in srgb, var(--pellet) 40%, transparent),
              inset 0 0 0 1.5px color-mix(in srgb, var(--pellet) 55%, transparent);
  animation: le-pulse 1.7s ease-in-out infinite;
  font-family: 'Space Grotesk', sans-serif;
}
@keyframes le-pulse { 0%,100%{transform:scale(0.92);opacity:0.9} 50%{transform:scale(1);opacity:1} }
.le-host .pellet.eaten { animation: le-pelletEaten 0.4s ease forwards !important; }
@keyframes le-pelletEaten { 0%{transform:scale(1);opacity:1} 100%{transform:scale(2);opacity:0} }

/* Characters */
.le-host .toon { position: absolute; inset: 0; }
.le-host .toon .body { position: absolute; inset: 0; }
.le-host .toon .eye {
  position: absolute; width: 28%; height: 28%;
  background: white; border-radius: 50%; top: 26%;
  box-shadow: inset 0 -1px 1px rgba(0,0,0,0.10); overflow: hidden;
}
.le-host .toon .eye.l { left: 16%; }
.le-host .toon .eye.r { right: 16%; }
.le-host .toon .eye::after {
  content: ''; position: absolute; width: 55%; height: 55%;
  background: #0A0F1C; border-radius: 50%; top: 22%; left: 22%;
  transition: transform 0.15s ease;
}
.le-host .toon.right .eye::after { transform: translateX(30%); }
.le-host .toon.left  .eye::after { transform: translateX(-30%); }
.le-host .toon.up    .eye::after { transform: translateY(-30%); }
.le-host .toon.down  .eye::after { transform: translateY(20%); }

.le-host .toon .mouth {
  position: absolute; left: 30%; right: 30%;
  bottom: 18%; height: 18%;
  background: #0A0F1C;
  border-radius: 0 0 999px 999px;
  transform: scaleY(0); transform-origin: top center;
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.le-host .toon.eating .mouth { transform: scaleY(1); }

/* Blob */
.le-host .toon[data-char="blob"] .body {
  border-radius: 50% 50% 45% 45% / 55% 55% 45% 45%;
  background: radial-gradient(circle at 30% 30%, #ff9bc5, #ff6b9d 50%, #b239e0 100%);
  box-shadow: 0 0 14px rgba(255,107,157,0.55), inset -2px -3px 4px rgba(0,0,0,0.18), inset 2px 2px 4px rgba(255,255,255,0.30);
  animation: le-squish 0.6s ease-in-out infinite alternate;
}
.le-host .toon[data-char="blob"]::before {
  content: ''; position: absolute; top: -22%; left: 50%;
  width: 8%; height: 24%; background: #b239e0; border-radius: 999px;
  transform: translateX(-50%);
}
.le-host .toon[data-char="blob"]::after {
  content: ''; position: absolute; top: -28%; left: 50%;
  width: 22%; height: 22%;
  background: radial-gradient(circle, #ffd60a, #ff6b9d);
  border-radius: 50%; transform: translateX(-50%);
  box-shadow: 0 0 8px rgba(255,214,10,0.7);
  animation: le-wiggle 0.7s ease-in-out infinite alternate;
}
@keyframes le-squish { 0%{transform:scale(1,1)} 100%{transform:scale(1.05,0.95)} }
@keyframes le-wiggle { 0%{transform:translateX(-70%)} 100%{transform:translateX(-30%)} }

/* Cat */
.le-host .toon[data-char="cat"] .body {
  background: radial-gradient(circle at 35% 35%, #ffb673, #f59e3a 60%, #d97706 100%);
  border-radius: 50%;
  box-shadow: 0 0 12px rgba(245,158,58,0.55), inset -2px -3px 4px rgba(0,0,0,0.20), inset 2px 2px 4px rgba(255,255,255,0.30);
}
.le-host .toon[data-char="cat"]::before,
.le-host .toon[data-char="cat"]::after {
  content: ''; position: absolute; top: -18%;
  width: 0; height: 0;
  border-left: 0.18em solid transparent;
  border-right: 0.18em solid transparent;
  border-bottom: 0.32em solid #f59e3a;
  font-size: calc(var(--char) * 1);
  filter: drop-shadow(0 -1px 1px rgba(0,0,0,0.15));
}
.le-host .toon[data-char="cat"]::before { left: 4%; transform: rotate(-15deg); }
.le-host .toon[data-char="cat"]::after  { right: 4%; transform: rotate(15deg); }

/* Robot */
.le-host .toon[data-char="robot"] .body {
  background: linear-gradient(135deg, #67e8f9 0%, #3b6cec 100%);
  border-radius: 22% 22% 18% 18%;
  box-shadow: 0 0 12px rgba(103,232,249,0.55), inset 0 -3px 0 rgba(0,0,0,0.18), inset 0 2px 0 rgba(255,255,255,0.30);
}
.le-host .toon[data-char="robot"]::before {
  content: ''; position: absolute; top: -22%; left: 50%;
  width: 8%; height: 22%; background: #67e8f9; border-radius: 999px;
  transform: translateX(-50%);
}
.le-host .toon[data-char="robot"]::after {
  content: ''; position: absolute; top: -28%; left: 50%;
  width: 18%; height: 18%;
  background: radial-gradient(circle, #ff6b9d, #b239e0);
  border-radius: 50%; transform: translateX(-50%);
  box-shadow: 0 0 6px rgba(255,107,157,0.7);
  animation: le-blink 1.2s ease-in-out infinite;
}
@keyframes le-blink { 0%,90%,100%{opacity:1} 93%{opacity:0.3} }
.le-host .toon[data-char="robot"] .eye { border-radius: 22%; }

/* Alien */
.le-host .toon[data-char="alien"] .body {
  background: radial-gradient(circle at 35% 30%, #a7f3d0, #10b981 60%, #047857 100%);
  border-radius: 50%;
  box-shadow: 0 0 14px rgba(16,185,129,0.55), inset -2px -3px 4px rgba(0,0,0,0.18), inset 2px 2px 4px rgba(255,255,255,0.30);
}
.le-host .toon[data-char="alien"]::before,
.le-host .toon[data-char="alien"]::after {
  content: ''; position: absolute; top: -26%;
  width: 6%; height: 28%; background: #10b981; border-radius: 999px;
  box-shadow: 0 -6px 0 -1px #d1fae5, 0 -6px 8px 0 rgba(167,243,208,0.8);
}
.le-host .toon[data-char="alien"]::before { left: 28%; transform: rotate(-12deg); }
.le-host .toon[data-char="alien"]::after  { right: 28%; transform: rotate(12deg); }

/* Pumpkin */
.le-host .toon[data-char="pumpkin"] .body {
  background: radial-gradient(circle at 35% 30%, #fdba74, #ea580c 60%, #9a3412 100%);
  border-radius: 45% 45% 50% 50% / 50% 50% 55% 55%;
  box-shadow: 0 0 12px rgba(234,88,12,0.55),
    inset 6px 0 0 -4px rgba(0,0,0,0.18), inset -6px 0 0 -4px rgba(0,0,0,0.18),
    inset -2px -3px 4px rgba(0,0,0,0.18), inset 2px 2px 4px rgba(255,255,255,0.20);
}
.le-host .toon[data-char="pumpkin"]::before {
  content: ''; position: absolute; top: -12%; left: 50%;
  width: 16%; height: 20%;
  background: linear-gradient(180deg, #166534, #14532d);
  border-radius: 3px 3px 2px 2px;
  transform: translateX(-50%) rotate(-6deg);
}

/* Frog */
.le-host .toon[data-char="frog"] .body {
  background: radial-gradient(circle at 35% 40%, #86efac, #16a34a 60%, #14532d 100%);
  border-radius: 50% 50% 55% 55% / 55% 55% 45% 45%;
  box-shadow: 0 0 12px rgba(22,163,74,0.55), inset -2px -3px 4px rgba(0,0,0,0.20), inset 2px 2px 4px rgba(255,255,255,0.30);
}
.le-host .toon[data-char="frog"] .eye {
  top: -2%; width: 38%; height: 38%;
  background: white;
  box-shadow: 0 0 0 2px #16a34a, inset 0 -1px 1px rgba(0,0,0,0.10);
}
.le-host .toon[data-char="frog"] .eye.l { left: 6%; }
.le-host .toon[data-char="frog"] .eye.r { right: 6%; }

/* Big toon on the maze */
.le-host .toon-big {
  position: absolute; width: var(--char); height: var(--char);
  transition: top 0.13s linear, left 0.13s linear;
  z-index: 10; pointer-events: none;
  filter: drop-shadow(0 0 3px rgba(255,107,157,0.30));
}
.le-host .toon-big[data-char-glow="cat"]     { filter: drop-shadow(0 0 3px rgba(245,158,58,0.30)); }
.le-host .toon-big[data-char-glow="robot"]   { filter: drop-shadow(0 0 3px rgba(103,232,249,0.30)); }
.le-host .toon-big[data-char-glow="alien"]   { filter: drop-shadow(0 0 3px rgba(16,185,129,0.30)); }
.le-host .toon-big[data-char-glow="pumpkin"] { filter: drop-shadow(0 0 3px rgba(234,88,12,0.30)); }
.le-host .toon-big[data-char-glow="frog"]    { filter: drop-shadow(0 0 3px rgba(22,163,74,0.30)); }
.le-host .toon-big .toon { position: absolute; inset: 0; }

/* Sentence box */
.le-host .sentence-box {
  background: var(--panel);
  backdrop-filter: blur(10px);
  border: 1px solid var(--panel);
  padding: 14px 20px; border-radius: 16px;
  text-align: center; width: 100%; max-width: 540px;
  transition: background 0.5s ease, border-color 0.5s ease;
}
.le-host .sentence-box.complete {
  animation: le-shimmer 1.2s ease-in-out;
  border-color: rgba(255,107,157,0.50);
  box-shadow: 0 0 30px rgba(255,107,157,0.25);
}
@keyframes le-shimmer {
  0%{box-shadow:0 0 0 rgba(255,107,157,0)} 50%{box-shadow:0 0 40px rgba(255,107,157,0.6)} 100%{box-shadow:0 0 30px rgba(255,107,157,0.25)}
}
.le-host .english {
  font-size: 11px; color: var(--muted); margin-bottom: 8px;
  text-transform: uppercase; letter-spacing: 2.5px; font-weight: 600;
}
.le-host .slots { display: flex; gap: 3px; justify-content: center; flex-wrap: wrap; }
.le-host .slot {
  min-width: 22px; height: 32px;
  border-bottom: 3px solid var(--panel);
  display: grid; place-items: center;
  font-weight: 700; font-size: 20px;
  color: var(--text); padding: 0 3px;
  transition: border-color 0.2s, color 0.2s;
}
.le-host .slot.filled {
  border-bottom-color: var(--accent);
  color: var(--accent);
  animation: le-pop 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.le-host .slot.space { border-bottom: none; min-width: 12px; }
@keyframes le-pop { 0%{transform:scale(1.8);opacity:0} 100%{transform:scale(1);opacity:1} }

/* Arabic / RTL */
.le-host.rtl .slots { direction: rtl; }
.le-host.rtl .slot { font-family: 'Noto Naskh Arabic', 'Amiri', serif; font-size: 24px; }
.le-host.rtl .pellet { font-family: 'Noto Naskh Arabic', 'Amiri', serif; font-size: calc(var(--cell) * 0.55); }

/* Controls */
.le-host .controls { display: flex; gap: 10px; min-height: 42px; align-items: center; }
.le-host button {
  background: linear-gradient(135deg, #3B6CEC, #B299E0);
  color: white; border: none;
  padding: 10px 22px; border-radius: 12px;
  font-family: inherit; font-weight: 700; font-size: 14px;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(59,108,236,0.40);
  transition: transform 0.15s, box-shadow 0.2s;
}
.le-host button:active { transform: translateY(1px); }
.le-host button.secondary { background: rgba(255,255,255,0.08); box-shadow: none; color: var(--muted); }

/* D-pad */
.le-host .dpad { display: none; position: relative; width: 168px; height: 168px; margin-top: 4px; }
.le-host .dpad button {
  position: absolute; width: 56px; height: 56px;
  padding: 0; border-radius: 14px;
  background: var(--panel); color: var(--text);
  font-size: 20px;
  box-shadow: 0 6px 14px rgba(0,0,0,0.30);
  display: grid; place-items: center;
  transition: background 0.5s ease, color 0.5s ease;
}
.le-host .dpad button:active { filter: brightness(1.4); }
.le-host .dpad .u { top: 0;    left: 56px; }
.le-host .dpad .d { bottom: 0; left: 56px; }
.le-host .dpad .l { top: 56px; left: 0; }
.le-host .dpad .r { top: 56px; right: 0; }
@media (max-width: 900px), (pointer: coarse) { .le-host .dpad { display: block; } }

.le-host .hint {
  font-size: 11px; color: var(--muted); text-align: center;
  background: var(--panel); padding: 5px 12px; border-radius: 999px;
}
.le-host .hint kbd {
  display: inline-block; background: var(--panel);
  padding: 1px 5px; border-radius: 4px;
  font-family: inherit; font-size: 10px; color: var(--text); margin: 0 1px;
}
@media (max-width: 640px) { .le-host .hint.desktop { display: none; } }
@media (min-width: 641px) { .le-host .hint.mobile { display: none; } }

/* Particles */
.le-host .particle {
  position: absolute; width: 8px; height: 8px;
  border-radius: 50%; pointer-events: none; z-index: 20;
  will-change: transform, opacity;
  animation: le-burst 0.7s cubic-bezier(0.18, 0.89, 0.32, 1.28) forwards;
}
@keyframes le-burst { 0%{transform:translate(0,0) scale(1);opacity:1} 100%{transform:translate(var(--dx), var(--dy)) scale(0);opacity:0} }

/* Stars */
.le-host .stars { position: absolute; inset: 0; pointer-events: none; z-index: 0; }
.le-host .star {
  position: absolute; width: 2px; height: 2px;
  background: var(--star); border-radius: 50%; opacity: 0.4;
  animation: le-twinkle 3s ease-in-out infinite;
}
@keyframes le-twinkle { 0%,100%{opacity:0.2;transform:scale(1)} 50%{opacity:0.9;transform:scale(1.5)} }

/* Drawer (fixed to viewport, outside host containment) */
.le-drawer-backdrop {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(8px);
  z-index: 1500;
  opacity: 0; pointer-events: none;
  transition: opacity 0.25s ease;
}
.le-drawer-backdrop.show { opacity: 1; pointer-events: auto; }
.le-drawer {
  position: fixed; top: 0; right: 0; bottom: 0;
  width: min(380px, 92vw);
  background: linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%);
  color: #0A0F1C;
  border-left: 1px solid rgba(10,15,28,0.08);
  box-shadow: -20px 0 60px rgba(0,0,0,0.25);
  transform: translateX(100%);
  transition: transform 0.32s cubic-bezier(0.34, 1.20, 0.64, 1);
  z-index: 1600;
  display: flex; flex-direction: column;
  overflow-y: auto;
  font-family: 'Space Grotesk', system-ui, sans-serif;
}
.le-drawer.show { transform: translateX(0); }
.le-drawer * { box-sizing: border-box; }
.le-drawer-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 22px; border-bottom: 1px solid rgba(10,15,28,0.08);
}
.le-drawer-head h2 { font-size: 18px; font-weight: 800; color: #0A0F1C; margin: 0; }
.le-drawer-close {
  width: 34px; height: 34px; border-radius: 50%;
  background: rgba(10,15,28,0.06);
  border: none; color: #0A0F1C; font-size: 16px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}
.le-drawer-close:hover { background: rgba(10,15,28,0.12); }
.le-drawer-section { padding: 18px 22px; border-bottom: 1px solid rgba(10,15,28,0.05); }
.le-drawer-section:last-child { border-bottom: none; }
.le-drawer-section .label {
  font-size: 11px; color: #5b6478;
  text-transform: uppercase; letter-spacing: 2px; font-weight: 700;
  margin-bottom: 12px;
}
.le-drawer-section .hint-text { font-size: 11px; color: #5b6478; margin-top: 10px; line-height: 1.5; }
.le-theme-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  justify-content: center;
}
.le-theme-swatch {
  aspect-ratio: 1.25;
  border-radius: 10px; border: 2px solid transparent;
  cursor: pointer; position: relative;
  transition: transform 0.15s, border-color 0.15s, box-shadow 0.15s;
  display: flex; align-items: flex-end; justify-content: center;
  padding: 0;
  font-size: 9px; font-weight: 700;
  box-shadow: 0 2px 6px rgba(0,0,0,0.10);
  min-width: 0;
  overflow: hidden;
  color: transparent;          /* hide the raw label text; we style it via the inner span */
}
/* Dark gradient strip at the bottom of every swatch — guarantees the label
   reads on any pastel background (light or dark) without per-theme tuning. */
.le-theme-swatch::before {
  content: '';
  position: absolute;
  left: 0; right: 0; bottom: 0;
  height: 50%;
  background: linear-gradient(to top, rgba(0,0,0,0.55), transparent);
  pointer-events: none;
  border-radius: 0 0 8px 8px;
}
/* The actual label — small, white, centered at the bottom */
.le-theme-swatch > span {
  position: relative; z-index: 1;
  color: white;
  font-size: 9px; font-weight: 700;
  letter-spacing: 0.3px;
  padding-bottom: 4px;
  text-shadow: 0 1px 0 rgba(0,0,0,0.45), 0 0 4px rgba(0,0,0,0.5);
}
.le-theme-swatch:hover { transform: translateY(-2px); box-shadow: 0 6px 14px rgba(0,0,0,0.18); }
.le-theme-swatch.active { border-color: #3B6CEC; box-shadow: 0 0 0 1.5px #3B6CEC, 0 6px 16px rgba(59,108,236,0.30); }
.le-char-picker { display: grid; grid-template-columns: repeat(6, 1fr); gap: 6px; }
.le-char-picker .pick {
  aspect-ratio: 1; width: 100%;
  border-radius: 12px;
  background: rgba(10,15,28,0.04);
  border: 2px solid rgba(10,15,28,0.06);
  cursor: pointer; position: relative;
  padding-top: 24%; padding-bottom: 10%; padding-left: 14%; padding-right: 14%;
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.15s, border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.le-char-picker .pick:hover { background: rgba(10,15,28,0.08); transform: translateY(-2px); }
.le-char-picker .pick.active { border-color: #3B6CEC; background: rgba(59,108,236,0.10); box-shadow: 0 4px 14px rgba(59,108,236,0.25); }
.le-char-picker .pick .mini { width: 100%; aspect-ratio: 1; position: relative; display: block; }
/* The picker character icons inherit the same .toon CSS via the global selectors above */
.le-sound-row { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.le-sound-row .row-text { flex: 1; min-width: 0; }
.le-sound-row .row-title { font-size: 14px; font-weight: 700; color: #0A0F1C; }
.le-sound-row .row-hint  { font-size: 11px; color: #5b6478; margin-top: 2px; }
.le-sound-toggle {
  position: relative; width: 48px; height: 26px;
  border-radius: 999px; background: rgba(10,15,28,0.18);
  border: none; cursor: pointer; flex-shrink: 0; padding: 0;
  transition: background 0.2s;
}
.le-sound-toggle::after {
  content: ''; position: absolute; top: 3px; left: 3px;
  width: 20px; height: 20px;
  background: white; border-radius: 50%;
  transition: transform 0.2s; box-shadow: 0 2px 4px rgba(0,0,0,0.20);
}
.le-sound-toggle.on { background: #3B6CEC; }
.le-sound-toggle.on::after { transform: translateX(22px); }
.le-script-picker {
  display: grid; grid-template-columns: 1fr 1fr; gap: 0;
  background: rgba(10,15,28,0.05); border-radius: 12px; padding: 4px;
}
.le-script-picker button {
  background: transparent; color: #0A0F1C; border: none;
  padding: 10px 12px; border-radius: 9px;
  font-family: inherit; font-weight: 700; font-size: 13px;
  cursor: pointer; box-shadow: none;
  transition: background 0.18s, color 0.18s;
}
.le-script-picker button.active { background: white; color: #3B6CEC; box-shadow: 0 2px 6px rgba(0,0,0,0.10); }
.le-script-picker button[data-script="arabic"] { font-family: 'Noto Naskh Arabic', 'Amiri', inherit; font-size: 16px; }

/* Confetti */
.le-confetti {
  position: fixed; width: 10px; height: 14px; top: -20px;
  pointer-events: none; z-index: 2000;
  will-change: transform;
  animation: le-fall var(--dur, 3s) cubic-bezier(0.2, 0.6, 0.8, 1) forwards;
}
@keyframes le-fall { 0%{transform:translate(0,0) rotate(0deg);opacity:1} 100%{transform:translate(var(--dx, 0), 110vh) rotate(720deg);opacity:0} }

/* Win popup */
.le-win-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
  z-index: 1800;
  display: none; align-items: center; justify-content: center;
  padding: 20px;
  animation: le-winFadeIn 0.3s ease;
}
.le-win-overlay.show { display: flex; }
@keyframes le-winFadeIn { from{opacity:0} to{opacity:1} }
.le-win-card {
  background: white; border-radius: 24px;
  padding: 32px 28px 24px; text-align: center;
  width: 100%; max-width: 420px;
  box-shadow: 0 30px 70px rgba(0,0,0,0.40);
  animation: le-winPop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1);
  font-family: 'Space Grotesk', system-ui, sans-serif;
}
.le-win-card * { box-sizing: border-box; }
@keyframes le-winPop { 0%{transform:scale(0.5) translateY(20px);opacity:0} 100%{transform:scale(1) translateY(0);opacity:1} }
.le-win-hero { font-size: 56px; margin-bottom: 6px; animation: le-winBounce 0.8s ease-in-out infinite alternate; }
@keyframes le-winBounce { from{transform:translateY(0) rotate(-6deg)} to{transform:translateY(-8px) rotate(6deg)} }
.le-win-title {
  font-size: 30px; font-weight: 900;
  background: linear-gradient(135deg, #ff6b9d, #b239e0, #3b6cec);
  -webkit-background-clip: text; background-clip: text; color: transparent;
  letter-spacing: -0.5px; margin: 4px 0 14px;
}
.le-win-sentence {
  font-size: 28px; font-weight: 800; color: #0A0F1C;
  padding: 14px 16px;
  background: linear-gradient(135deg, rgba(59,108,236,0.08), rgba(178,153,224,0.08));
  border-radius: 14px; line-height: 1.3;
  margin-bottom: 8px; word-break: break-word;
}
.le-win-sentence.arabic { font-family: 'Noto Naskh Arabic', 'Amiri', serif; direction: rtl; font-size: 32px; }
.le-win-english {
  font-size: 13px; color: #5b6478;
  text-transform: uppercase; letter-spacing: 2px; font-weight: 600;
  margin-bottom: 22px;
}
.le-win-next {
  background: linear-gradient(135deg, #ffd60a, #ff6b9d);
  color: #0A0F1C; border: none;
  padding: 14px 32px; border-radius: 999px;
  font-weight: 800; font-size: 15px;
  cursor: pointer;
  box-shadow: 0 12px 28px rgba(255,107,157,0.40);
  transition: transform 0.15s, box-shadow 0.2s;
  font-family: inherit;
}
.le-win-next:hover { transform: translateY(-2px); box-shadow: 0 16px 34px rgba(255,107,157,0.55); }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────
const LetterEater = ({ API, userToken, isDarkMode, showNotification, onBack, hasPaidPlan }) => {
  // Persisted user choices
  const initChar = (() => {
    try {
      const v = localStorage.getItem('letterEaterChar');
      return ['blob', 'cat', 'robot', 'alien', 'pumpkin', 'frog'].includes(v) ? v : 'blob';
    } catch { return 'blob'; }
  })();
  const initScript = (() => { try { return localStorage.getItem('letterEaterScript') === 'arabic' ? 'arabic' : 'latin'; } catch { return 'latin'; } })();
  const initThemeIdx = (() => {
    try {
      const v = parseInt(localStorage.getItem('letterEaterBg') || '1', 10);
      if (isNaN(v) || v < 0 || v >= THEMES.length) return 1;
      return v;
    } catch { return 1; }
  })();

  const [settings, setSettings] = useState(null);
  const [sentences, setSentences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const [currentChar, setCurrentChar] = useState(initChar);
  const [currentScript, setCurrentScript] = useState(initScript);
  const [currentThemeIdx, setCurrentThemeIdx] = useState(initThemeIdx);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [winOpen, setWinOpen] = useState(false);
  const [winSentenceText, setWinSentenceText] = useState('');
  const [winEnglish, setWinEnglish] = useState('');
  const [winIsArabic, setWinIsArabic] = useState(false);
  const [, forceRender] = useState(0);

  // Refs for imperative game state
  const hostRef = useRef(null);
  const mazeRef = useRef(null);
  const bigElRef = useRef(null);
  const sentenceBoxRef = useRef(null);
  const slotsContainerRef = useRef(null);

  const sfxRef = useRef(null);
  const [soundOn, setSoundOn] = useState(true);

  const pelletsRef = useRef([]);
  const filledRef = useRef([]);
  const pacRef = useRef({ ...PAC_SPAWN, dir: 'right' });
  const nextDirRef = useRef('right');
  const tickTimerRef = useRef(null);
  const teleportingRef = useRef(false);
  const mazeLayoutRef = useRef(MAZE_LAYOUTS[0]);
  const sentenceIdxRef = useRef(0);
  const scriptRef = useRef(initScript);
  const charRef = useRef(initChar);

  // Init sound engine once
  if (!sfxRef.current) {
    sfxRef.current = makeSfx();
    setTimeout(() => setSoundOn(sfxRef.current.enabled()), 0);
  }

  // ─── Apply theme to host (CSS variables + wall style attr) ─────────
  const applyTheme = useCallback((t) => {
    const host = hostRef.current;
    if (!host) return;
    if (t.gradient) host.style.setProperty('--bg-gradient', t.gradient);
    if (t.text)   host.style.setProperty('--text', t.text);
    if (t.muted)  host.style.setProperty('--muted', t.muted);
    if (t.panel)  host.style.setProperty('--panel', t.panel);
    if (t.accent) host.style.setProperty('--accent', t.accent);
    if (t.pellet) host.style.setProperty('--pellet', t.pellet);
    if (t.star)   host.style.setProperty('--star', t.star);
    if (t.wall)      host.style.setProperty('--wall', t.wall);
    if (t.wallEdge)  host.style.setProperty('--wall-edge', t.wallEdge);
    if (t.wallStyle) host.setAttribute('data-wall-style', t.wallStyle);
    else             host.removeAttribute('data-wall-style');
  }, []);

  // ─── Load settings + sentences from backend ────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const settingsRes = await fetch(`${API}/letter-eater/game-settings`);
        if (!settingsRes.ok) throw new Error('Failed to load game settings');
        const settingsData = await settingsRes.json();
        if (!alive) return;
        setSettings(settingsData);

        // Apply admin defaults if user has not picked yet
        let didSetChar = false, didSetScript = false, didSetTheme = false;
        try {
          if (!localStorage.getItem('letterEaterChar') && settingsData.default_character && CHARACTERS.find(c => c.id === settingsData.default_character)) {
            setCurrentChar(settingsData.default_character);
            charRef.current = settingsData.default_character;
            didSetChar = true;
          }
          if (!localStorage.getItem('letterEaterScript') && (settingsData.default_script === 'latin' || settingsData.default_script === 'arabic')) {
            setCurrentScript(settingsData.default_script);
            scriptRef.current = settingsData.default_script;
            didSetScript = true;
          }
          if (!localStorage.getItem('letterEaterBg') && settingsData.default_theme) {
            const idx = THEMES.findIndex(t => t.name === settingsData.default_theme);
            if (idx >= 0) { setCurrentThemeIdx(idx); didSetTheme = true; }
          }
        } catch {}

        // Sentences
        const headers = userToken ? { Authorization: `Bearer ${userToken}` } : {};
        const sentencesRes = await fetch(`${API}/letter-eater/sentences`, { headers });
        if (sentencesRes.status === 403) {
          if (alive) { setErrorMsg('pro_required'); setLoading(false); }
          return;
        }
        if (!sentencesRes.ok) throw new Error('Failed to load sentences');
        const d = await sentencesRes.json();
        if (!alive) return;
        const list = d.sentences || [];
        if (list.length === 0) {
          setErrorMsg('no_sentences');
          setLoading(false);
          return;
        }
        setSentences(list);
        // Start with a random sentence
        sentenceIdxRef.current = Math.floor(Math.random() * list.length);
        setLoading(false);
      } catch (e) {
        if (alive) {
          setErrorMsg('load_failed');
          setLoading(false);
          showNotification?.(`Failed to load Letter Eater: ${e.message}`, 'error');
        }
      }
    })();
    return () => { alive = false; };
    // Intentionally exclude `showNotification` — parent re-renders pass a new
    // function reference each time, which would re-fire this effect mid-play,
    // refetch sentences, pick a new random one, and make the English label
    // visibly switch every time the character eats a letter.
  }, [API, userToken]);

  // ─── Apply theme whenever it changes ────────────────────────────────
  useEffect(() => {
    applyTheme(THEMES[currentThemeIdx]);
  }, [currentThemeIdx, applyTheme]);

  // ─── Build stars once after mount (theme can update --star color) ──
  useEffect(() => {
    if (loading || errorMsg) return;
    const host = hostRef.current;
    if (!host) return;
    const starsEl = host.querySelector('.stars');
    if (!starsEl) return;
    starsEl.innerHTML = '';
    for (let i = 0; i < 60; i++) {
      const s = document.createElement('div');
      s.className = 'star';
      s.style.left = Math.random() * 100 + 'vw';
      s.style.top  = Math.random() * 100 + 'vh';
      s.style.animationDelay = (Math.random() * 3) + 's';
      s.style.animationDuration = (2 + Math.random() * 3) + 's';
      s.style.opacity = (0.3 + Math.random() * 0.5);
      starsEl.appendChild(s);
    }
  }, [loading, errorMsg]);

  // ─── Helpers ───────────────────────────────────────────────────────
  const cellPx = useCallback(() => {
    const host = hostRef.current;
    if (!host) return 38;
    return parseInt(getComputedStyle(host).getPropertyValue('--cell')) || 38;
  }, []);
  const charPxFn = useCallback(() => {
    const host = hostRef.current;
    if (!host) return 32;
    return parseInt(getComputedStyle(host).getPropertyValue('--char')) || 32;
  }, []);

  const buildMaze = useCallback(() => {
    const maze = mazeRef.current;
    if (!maze) return;
    maze.innerHTML = '';
    const MAZE = mazeLayoutRef.current;
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        const cell = document.createElement('div');
        cell.className = 'cell' + (MAZE[r][c] === 1 ? ' wall' : '');
        maze.appendChild(cell);
      }
    }
  }, []);

  const placePellets = useCallback((target) => {
    const maze = mazeRef.current;
    if (!maze) return;
    pelletsRef.current = [];
    const letters = target.split('').filter((ch) => ch !== ' ');
    const open = [];
    const MAZE = mazeLayoutRef.current;
    for (let r = 0; r < ROWS; r++) {
      for (let c = 0; c < COLS; c++) {
        if (MAZE[r][c] === 0 && !(r === PAC_SPAWN.r && c === PAC_SPAWN.c)) open.push({ r, c });
      }
    }
    for (let i = open.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [open[i], open[j]] = [open[j], open[i]];
    }
    for (let i = 0; i < letters.length && i < open.length; i++) {
      pelletsRef.current.push({ r: open[i].r, c: open[i].c, letter: letters[i] });
    }
    // Render
    maze.querySelectorAll('.pellet').forEach((el) => el.remove());
    for (const p of pelletsRef.current) {
      const cell = maze.children[p.r * COLS + p.c];
      if (!cell) continue;
      const dot = document.createElement('div');
      dot.className = 'pellet';
      dot.textContent = p.letter;
      cell.appendChild(dot);
      p.el = dot;
    }
  }, []);

  const toonInner = '<div class="body"></div><div class="eye l"></div><div class="eye r"></div><div class="mouth"></div>';

  const spawnBig = useCallback(() => {
    const maze = mazeRef.current;
    if (!maze) return;
    if (bigElRef.current) bigElRef.current.remove();
    const el = document.createElement('div');
    el.className = 'toon-big';
    el.innerHTML = '<div class="toon right" data-char="' + charRef.current + '">' + toonInner + '</div>';
    maze.appendChild(el);
    bigElRef.current = el;
    positionBig();
  }, []);

  const positionBig = useCallback((skipTransition) => {
    const el = bigElRef.current;
    const maze = mazeRef.current;
    if (!el || !maze) return;
    const cp = cellPx(), ch = charPxFn();
    if (skipTransition) {
      el.style.transition = 'none';
      el.style.opacity = '0';
    }
    el.style.left = (pacRef.current.c * cp + (cp - ch) / 2) + 'px';
    el.style.top  = (pacRef.current.r * cp + (cp - ch) / 2) + 'px';
    el.setAttribute('data-char-glow', charRef.current);
    const t = el.querySelector('.toon');
    if (t) {
      const eating = t.classList.contains('eating');
      t.className = 'toon ' + pacRef.current.dir + (eating ? ' eating' : '');
      t.setAttribute('data-char', charRef.current);
    }
    maze.style.setProperty('--gx', (pacRef.current.c * cp + cp / 2) + 'px');
    maze.style.setProperty('--gy', (pacRef.current.r * cp + cp / 2) + 'px');
    if (skipTransition) {
      requestAnimationFrame(() => {
        if (!el) return;
        el.style.transition = '';
        el.style.opacity = '';
      });
    }
  }, [cellPx, charPxFn]);

  const triggerEat = useCallback(() => {
    const el = bigElRef.current;
    if (!el) return;
    const t = el.querySelector('.toon');
    if (!t) return;
    t.classList.add('eating');
    setTimeout(() => {
      if (bigElRef.current) {
        const t2 = bigElRef.current.querySelector('.toon');
        if (t2) t2.classList.remove('eating');
      }
    }, 450);
  }, []);

  const buildSlots = useCallback((target) => {
    const c = slotsContainerRef.current;
    if (!c) return;
    c.innerHTML = '';
    filledRef.current = [];
    for (let i = 0; i < target.length; i++) {
      const ch = target[i];
      const slot = document.createElement('div');
      if (ch === ' ') { slot.className = 'slot space'; }
      else { slot.className = 'slot'; filledRef.current.push({ ch, done: false, el: slot }); }
      c.appendChild(slot);
    }
  }, []);

  const fillSlot = useCallback((letter) => {
    for (const f of filledRef.current) {
      if (!f.done && f.ch === letter) {
        f.done = true;
        f.el.classList.add('filled');
        f.el.textContent = letter;
        return true;
      }
    }
    return false;
  }, []);

  const isWordDone = useCallback(() => filledRef.current.every((f) => f.done), []);

  const canMove = useCallback((r, c, dir) => {
    let nr = r, nc = c;
    if (dir === 'left')  nc--;
    if (dir === 'right') nc++;
    if (dir === 'up')    nr--;
    if (dir === 'down')  nr++;
    const MAZE = mazeLayoutRef.current;
    if (dir === 'left'  && nc < 0    && MAZE[r][COLS - 1] === 0 && MAZE[r][0]        === 0) return { ok: true, nr: r, nc: COLS - 1 };
    if (dir === 'right' && nc >= COLS && MAZE[r][0]        === 0 && MAZE[r][COLS - 1] === 0) return { ok: true, nr: r, nc: 0 };
    if (nr < 0 || nr >= ROWS || nc < 0 || nc >= COLS) return { ok: false };
    if (MAZE[nr][nc] === 1) return { ok: false };
    return { ok: true, nr, nc };
  }, []);

  const spawnParticles = useCallback((r, c) => {
    const maze = mazeRef.current;
    if (!maze) return;
    const cp = cellPx();
    const cx = c * cp + cp / 2;
    const cy = r * cp + cp / 2;
    for (let i = 0; i < 8; i++) {
      const p = document.createElement('div');
      p.className = 'particle';
      p.style.background = PARTICLE_COLORS[Math.floor(Math.random() * PARTICLE_COLORS.length)];
      p.style.left = (cx - 4) + 'px';
      p.style.top  = (cy - 4) + 'px';
      p.style.boxShadow = '0 0 8px ' + p.style.background;
      const angle = (Math.PI * 2 * i) / 8 + Math.random() * 0.4;
      const dist  = 24 + Math.random() * 14;
      p.style.setProperty('--dx', Math.cos(angle) * dist + 'px');
      p.style.setProperty('--dy', Math.sin(angle) * dist + 'px');
      maze.appendChild(p);
      setTimeout(() => p.remove(), 800);
    }
  }, [cellPx]);

  const spawnConfetti = useCallback((count) => {
    count = count || 40;
    for (let i = 0; i < count; i++) {
      const c = document.createElement('div');
      c.className = 'le-confetti';
      c.style.background = CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)];
      c.style.left = Math.random() * 100 + 'vw';
      c.style.setProperty('--dx', (Math.random() - 0.5) * 200 + 'px');
      c.style.setProperty('--dur', (2.4 + Math.random() * 1.4) + 's');
      c.style.animationDelay = (Math.random() * 0.4) + 's';
      document.body.appendChild(c);
      setTimeout(() => c.remove(), 4500);
    }
  }, []);

  const winSentenceFn = useCallback(() => {
    if (tickTimerRef.current) { clearInterval(tickTimerRef.current); tickTimerRef.current = null; }
    if (sentenceBoxRef.current) sentenceBoxRef.current.classList.add('complete');
    spawnConfetti(50);
    sfxRef.current?.win();
    const s = sentences[sentenceIdxRef.current];
    if (!s) return;
    const isArabic = (scriptRef.current === 'arabic') && s.arabic;
    setWinSentenceText(isArabic ? s.arabic : s.latin);
    setWinEnglish(s.english);
    setWinIsArabic(!!isArabic);
    setWinOpen(true);
  }, [sentences, spawnConfetti]);

  const doTeleport = useCallback((newR, newC) => {
    const el = bigElRef.current;
    if (!el) return;
    teleportingRef.current = true;
    el.style.transition = 'opacity 0.15s ease';
    el.style.opacity = '0';
    setTimeout(() => {
      if (!bigElRef.current) { teleportingRef.current = false; return; }
      pacRef.current.r = newR; pacRef.current.c = newC;
      el.style.transition = 'none';
      positionBig();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!bigElRef.current) { teleportingRef.current = false; return; }
          el.style.transition = 'opacity 0.18s ease';
          el.style.opacity = '1';
          setTimeout(() => {
            if (bigElRef.current) bigElRef.current.style.transition = '';
            teleportingRef.current = false;
          }, 200);
        });
      });
    }, 160);
  }, [positionBig]);

  const tick = useCallback(() => {
    if (teleportingRef.current) return;
    let res = canMove(pacRef.current.r, pacRef.current.c, nextDirRef.current);
    if (res.ok) pacRef.current.dir = nextDirRef.current;
    else        res = canMove(pacRef.current.r, pacRef.current.c, pacRef.current.dir);
    if (!res.ok) return;
    const wrapped = Math.abs(res.nc - pacRef.current.c) > 1;
    if (wrapped) {
      doTeleport(res.nr, res.nc);
      return;
    }
    pacRef.current.r = res.nr; pacRef.current.c = res.nc;
    positionBig();
    const idx = pelletsRef.current.findIndex((p) => p.r === pacRef.current.r && p.c === pacRef.current.c);
    if (idx >= 0) {
      const p = pelletsRef.current[idx];
      fillSlot(p.letter);
      spawnParticles(p.r, p.c);
      sfxRef.current?.eat();
      if (p.el) {
        p.el.classList.add('eaten');
        const eaten = p.el;
        setTimeout(() => eaten.remove(), 400);
      }
      pelletsRef.current.splice(idx, 1);
      triggerEat();
      if (isWordDone()) winSentenceFn();
    }
  }, [canMove, doTeleport, fillSlot, isWordDone, positionBig, spawnParticles, triggerEat, winSentenceFn]);

  const loadSentence = useCallback((i) => {
    const s = sentences[i];
    if (!s) return;
    const isArabic = (scriptRef.current === 'arabic') && s.arabic;
    const target = (isArabic ? s.arabic : s.latin).toLowerCase();
    if (hostRef.current) hostRef.current.classList.toggle('rtl', !!isArabic);
    if (sentenceBoxRef.current) sentenceBoxRef.current.classList.remove('complete');
    pacRef.current = { ...PAC_SPAWN, dir: 'right' };
    nextDirRef.current = 'right';
    mazeLayoutRef.current = MAZE_LAYOUTS[Math.floor(Math.random() * MAZE_LAYOUTS.length)];
    buildMaze();
    buildSlots(target);
    placePellets(target);
    spawnBig();
    setWinOpen(false);
    if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    tickTimerRef.current = setInterval(tick, TICK_MS);
    forceRender(x => x + 1);  // re-render so the english label updates
  }, [sentences, buildMaze, buildSlots, placePellets, spawnBig, tick]);

  const pickRandomSentenceIdx = useCallback(() => {
    if (sentences.length <= 1) return 0;
    let next;
    do { next = Math.floor(Math.random() * sentences.length); }
    while (next === sentenceIdxRef.current);
    return next;
  }, [sentences.length]);

  // Boot the game once sentences are loaded
  useEffect(() => {
    if (loading || errorMsg || sentences.length === 0) return;
    loadSentence(sentenceIdxRef.current);
    return () => {
      if (tickTimerRef.current) clearInterval(tickTimerRef.current);
    };
  }, [loading, errorMsg, sentences.length]);

  // Keyboard input
  useEffect(() => {
    const KEY_MAP = {
      ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down',
      a: 'left', d: 'right', w: 'up', s: 'down',
      A: 'left', D: 'right', W: 'up', S: 'down',
    };
    const onKey = (e) => {
      const dir = KEY_MAP[e.key];
      if (dir) { nextDirRef.current = dir; sfxRef.current?.init(); e.preventDefault(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Touch swipe on maze
  useEffect(() => {
    const maze = mazeRef.current;
    if (!maze) return;
    let touchStart = null;
    const onStart = (e) => { const t = e.touches[0]; touchStart = { x: t.clientX, y: t.clientY }; };
    const onEnd = (e) => {
      if (!touchStart) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - touchStart.x, dy = t.clientY - touchStart.y;
      const ax = Math.abs(dx), ay = Math.abs(dy);
      if (Math.max(ax, ay) < 20) { touchStart = null; return; }
      if (ax > ay) nextDirRef.current = dx > 0 ? 'right' : 'left';
      else         nextDirRef.current = dy > 0 ? 'down'  : 'up';
      sfxRef.current?.init();
      touchStart = null;
    };
    maze.addEventListener('touchstart', onStart, { passive: true });
    maze.addEventListener('touchend', onEnd, { passive: true });
    return () => { maze.removeEventListener('touchstart', onStart); maze.removeEventListener('touchend', onEnd); };
  }, [loading, errorMsg]);

  // ─── Handlers ──────────────────────────────────────────────────────
  const handleNext = () => {
    setWinOpen(false);
    const next = pickRandomSentenceIdx();
    sentenceIdxRef.current = next;
    loadSentence(next);
  };
  const handleReset = () => {
    const next = pickRandomSentenceIdx();
    sentenceIdxRef.current = next;
    loadSentence(next);
  };
  const handleScriptChange = (s) => {
    if (s === scriptRef.current) return;
    scriptRef.current = s;
    setCurrentScript(s);
    try { localStorage.setItem('letterEaterScript', s); } catch {}
    loadSentence(sentenceIdxRef.current);
  };
  const handleCharChange = (id) => {
    charRef.current = id;
    setCurrentChar(id);
    try { localStorage.setItem('letterEaterChar', id); } catch {}
    const t = bigElRef.current && bigElRef.current.querySelector('.toon');
    if (t) t.setAttribute('data-char', id);
    if (bigElRef.current) bigElRef.current.setAttribute('data-char-glow', id);
  };
  const handleThemeChange = (i) => {
    setCurrentThemeIdx(i);
    try { localStorage.setItem('letterEaterBg', String(i)); } catch {}
  };
  const handleSoundToggle = () => {
    sfxRef.current.setEnabled(!sfxRef.current.enabled());
    setSoundOn(sfxRef.current.enabled());
    if (sfxRef.current.enabled()) sfxRef.current.eat();
  };
  const handleDpad = (dir) => { nextDirRef.current = dir; sfxRef.current?.init(); };

  // ─── Render ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div ref={hostRef} className="le-host">
        <style>{STYLE}</style>
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', fontWeight: 700 }}>
          Loading Letter Eater…
        </div>
      </div>
    );
  }

  if (errorMsg === 'pro_required') {
    return (
      <div ref={hostRef} className="le-host">
        <style>{STYLE}</style>
        <button className="icon-btn back-btn" onClick={onBack} style={{ position: 'absolute', top: 16, left: 16, zIndex: 20 }} aria-label="Back to games">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 64, marginBottom: 12 }}>🔒</div>
          <h2 style={{ fontWeight: 800, fontSize: 28, color: '#0A0F1C', margin: '0 0 12px' }}>Pro feature</h2>
          <p style={{ color: '#8A8E9A', maxWidth: 320, lineHeight: 1.6 }}>Letter Eater is a Pro game. Upgrade to play.</p>
        </div>
      </div>
    );
  }

  if (errorMsg === 'no_sentences') {
    return (
      <div ref={hostRef} className="le-host">
        <style>{STYLE}</style>
        <button className="icon-btn back-btn" onClick={onBack} style={{ position: 'absolute', top: 16, left: 16, zIndex: 20 }} aria-label="Back to games">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 20 }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>👾</div>
          <h2 style={{ fontWeight: 800, fontSize: 24, color: '#0A0F1C', margin: '0 0 8px' }}>No sentences yet</h2>
          <p style={{ color: '#8A8E9A', maxWidth: 340, lineHeight: 1.6 }}>The admin hasn't added any sentences for Letter Eater. Check back soon!</p>
        </div>
      </div>
    );
  }

  const currentSentence = sentences[sentenceIdxRef.current];
  const isArabic = (currentScript === 'arabic') && currentSentence?.arabic;

  return (
    <div ref={hostRef} className={'le-host' + (isArabic ? ' rtl' : '')}>
      <style>{STYLE}</style>

      <div className="stars" />

      <div className="app">
        <div className="topbar">
          <button className="icon-btn back-btn" onClick={onBack} aria-label="Back to games">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
          </button>
          <h1>
            <span className="title-icon" aria-hidden="true">
              <span className="toon right" data-char="blob">
                <span className="body"></span><span className="eye l"></span><span className="eye r"></span>
              </span>
            </span>
            {settings?.name || 'Letter Eater'}
          </h1>
          <button className="icon-btn gear-btn" onClick={() => setDrawerOpen(true)} aria-label="Settings">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
          </button>
        </div>

        <div className="meta">
          <span>Eat every letter to fill the sentence</span>
        </div>

        <div className="maze-wrap">
          <div className="maze" ref={mazeRef} />
        </div>

        <div className="sentence-box" ref={sentenceBoxRef}>
          <div className="english">{currentSentence?.english || ''}</div>
          <div className="slots" ref={slotsContainerRef} />
        </div>

        <div className="controls">
          <button className="secondary" onClick={handleReset}>Reset</button>
        </div>

        <div className="dpad">
          <button className="u" onClick={() => handleDpad('up')} onTouchStart={(e) => { e.preventDefault(); handleDpad('up'); }}>▲</button>
          <button className="l" onClick={() => handleDpad('left')} onTouchStart={(e) => { e.preventDefault(); handleDpad('left'); }}>◀</button>
          <button className="r" onClick={() => handleDpad('right')} onTouchStart={(e) => { e.preventDefault(); handleDpad('right'); }}>▶</button>
          <button className="d" onClick={() => handleDpad('down')} onTouchStart={(e) => { e.preventDefault(); handleDpad('down'); }}>▼</button>
        </div>

        <div className="hint desktop"><kbd>←</kbd> <kbd>↑</kbd> <kbd>→</kbd> <kbd>↓</kbd> arrow keys to move • walk over a letter to eat it</div>
        <div className="hint mobile">Tap arrows or swipe on the maze • walk over a letter to eat it</div>
      </div>

      {/* Settings drawer (outside .app so it's not clipped by host overflow) */}
      <div className={`le-drawer-backdrop${drawerOpen ? ' show' : ''}`} onClick={() => setDrawerOpen(false)} />
      <aside className={`le-drawer${drawerOpen ? ' show' : ''}`} aria-hidden={!drawerOpen}>
        <header className="le-drawer-head">
          <h2>Settings</h2>
          <button className="le-drawer-close" onClick={() => setDrawerOpen(false)}>✕</button>
        </header>
        <div className="le-drawer-section">
          <div className="label">Character</div>
          <div className="le-char-picker">
            {CHARACTERS.map(c => (
              <button
                key={c.id}
                className={'pick' + (currentChar === c.id ? ' active' : '')}
                title={c.label}
                onClick={() => handleCharChange(c.id)}
              >
                <div className="mini">
                  <div className="toon right" data-char={c.id}>
                    <div className="body"></div>
                    <div className="eye l"></div>
                    <div className="eye r"></div>
                    <div className="mouth"></div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="le-drawer-section">
          <div className="label">Script</div>
          <div className="le-script-picker">
            <button data-script="latin" className={currentScript === 'latin' ? 'active' : ''} onClick={() => handleScriptChange('latin')}>Latin · salam</button>
            <button data-script="arabic" className={currentScript === 'arabic' ? 'active' : ''} onClick={() => handleScriptChange('arabic')}>عربي · سلام</button>
          </div>
          <div className="hint-text">Latin reads left-to-right. Arabic reads right-to-left.</div>
        </div>
        <div className="le-drawer-section">
          <div className="label">Background</div>
          <div className="le-theme-grid">
            {THEMES.map((t, i) => (
              <button
                key={t.name}
                className={'le-theme-swatch' + (currentThemeIdx === i ? ' active' : '')}
                style={{ background: t.gradient }}
                onClick={() => handleThemeChange(i)}
              >
                <span>{t.name}</span>
              </button>
            ))}
          </div>
          <div className="hint-text">Background and block colors both follow your theme. The maze layout shuffles each sentence.</div>
        </div>
        <div className="le-drawer-section">
          <div className="label">Sound</div>
          <div className="le-sound-row">
            <div className="row-text">
              <div className="row-title">Sound effects</div>
              <div className="row-hint">Pop on each letter • Happy cheer when you finish a sentence</div>
            </div>
            <button className={'le-sound-toggle' + (soundOn ? ' on' : '')} onClick={handleSoundToggle} aria-label="Toggle sound" />
          </div>
        </div>
      </aside>

      {/* Win popup */}
      <div className={'le-win-overlay' + (winOpen ? ' show' : '')}>
        <div className="le-win-card">
          <div className="le-win-hero">🎉</div>
          <div className="le-win-title">Wow, you did it!</div>
          <div className={'le-win-sentence' + (winIsArabic ? ' arabic' : '')}>{winSentenceText}</div>
          <div className="le-win-english">{winEnglish}</div>
          <button className="le-win-next" onClick={handleNext}>Next sentence →</button>
        </div>
      </div>
    </div>
  );
};

export default LetterEater;
